'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { signIn, signOut, useSession } from 'next-auth/react';
import { LoginTwoFactorModal } from '@/components/auth/LoginTwoFactorModal';
import '@/framer/home-login-styles.css';
import { HomeLoginInteractive } from '@/components/auth/HomeLoginInteractive';
import { getDeviceFingerprint } from '@/lib/utils/device-fingerprint';
import OnboardingModal from '@/components/onboarding/OnboardingModal';
import type { OnboardingData } from '@/components/onboarding/OnboardingModal';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type AuthVariant = 'login' | 'signup';

export default function Home() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [variant, setVariant] = useState<AuthVariant>('login');
  const [isSaveLoginEnabled, setIsSaveLoginEnabled] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const [deviceFingerprint, setDeviceFingerprint] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<string>('totp');
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorPhone, setTwoFactorPhone] = useState('');
  const twoFactorReturnFocusRef = useRef<HTMLElement | null>(null);
  const { update: updateSession } = useSession();

  // ── Signup onboarding modal state ──────────────────────────────────
  const [showSignupOnboarding, setShowSignupOnboarding] = useState(false);

  // Initialize device fingerprint on mount
  useEffect(() => {
    console.log('[Login] Starting device fingerprint initialization...');
    getDeviceFingerprint().then((fp) => {
      console.log('[Login] Device fingerprint initialized:', fp?.substring(0, 20) + '...');
      setDeviceFingerprint(fp);
    }).catch((err) => {
      console.error('[Login] Failed to get device fingerprint:', err);
      // Fallback: generate a simple random fingerprint
      const fallbackFp = 'fp_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      console.log('[Login] Using fallback fingerprint:', fallbackFp);
      setDeviceFingerprint(fallbackFp);
    });
  }, []);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setError('');
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
    setError('');
  }, []);

  const handleConfirmPasswordChange = useCallback((value: string) => {
    setConfirmPassword(value);
    setError('');
  }, []);

  const handleVariantChange = useCallback((newVariant: AuthVariant) => {
    setVariant(newVariant);
    setError(''); // Clear errors when switching variants
    setConfirmPassword(''); // Clear confirm password when switching
  }, []);

  const handleSaveLoginChange = useCallback((enabled: boolean) => {
    setIsSaveLoginEnabled(enabled);
    console.log('Save login changed:', enabled);
  }, []);

  const getTwoFactorFallbackFocusElement = useCallback(() => {
    return document.querySelector<HTMLElement>('[data-password-input="true"]')
      ?? document.querySelector<HTMLElement>('[data-email-input="true"]');
  }, []);

  const restoreTwoFactorFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const returnTarget = twoFactorReturnFocusRef.current;
        if (returnTarget?.isConnected && returnTarget !== document.body) {
          returnTarget.focus({ preventScroll: true });
        }

        if (returnTarget === document.body || document.activeElement !== returnTarget) {
          getTwoFactorFallbackFocusElement()?.focus({ preventScroll: true });
        }
      });
    });
  }, [getTwoFactorFallbackFocusElement]);

  // Handle account selection from Switch Accounts overlay
  const handleAccountSelect = useCallback((account: { email: string | null }) => {
    if (account.email) {
      setEmail(account.email);
      // Focus password field after email is filled
      // The browser's password manager / biometrics will handle password autofill
      setTimeout(() => {
        const passwordInput = document.querySelector('[data-password-input]') as HTMLInputElement;
        if (passwordInput) {
          passwordInput.focus();
        }
      }, 100);
    }
  }, []);

  const handleLogin = useCallback(async () => {
    console.log('[Login] handleLogin called with email:', email, 'password:', password ? '***' : '(empty)');
    twoFactorReturnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    
    if (!email || !password) {
      console.log('[Login] Missing email or password');
      setError('Please enter email and password');
      return;
    }

    setIsLoggingIn(true);
    setError('');

    try {
      console.log('[Login] Calling signIn with credentials');
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });
      console.log('[Login] signIn result:', JSON.stringify(result, null, 2));

      if (result?.error) {
        console.log('[Login] Error from signIn:', result.error);
        setError('Invalid credentials');
      } else if (result?.ok) {
        console.log('[Login] Login successful, checking 2FA status...');

        // Fetch the fresh session to check 2FA status
        try {
          const sessionRes = await fetch('/api/auth/session', { credentials: 'include' });
          const sessionData = await sessionRes.json();
          console.log('[Login] Session data:', JSON.stringify(sessionData, null, 2));

          if (sessionData?.user?.twoFactorEnabled && !sessionData?.user?.twoFactorVerified) {
            // 2FA is enabled — show verification modal instead of redirecting
            console.log('[Login] 2FA required, showing modal. Method:', sessionData.user.twoFactorMethod);
            setTwoFactorMethod(sessionData.user.twoFactorMethod || 'totp');
            setTwoFactorEmail(sessionData.user.email || email);
            setTwoFactorPhone(sessionData.user.phone || '');
            setShowTwoFactor(true);
            setIsLoggingIn(false);
            return; // Don't redirect yet
          }
        } catch (sessionErr) {
          console.error('[Login] Failed to fetch session for 2FA check:', sessionErr);
          // Continue with normal flow if session fetch fails
        }

        // No 2FA required — proceed with normal login flow
        await completeLoginFlow();
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  }, [email, password, isSaveLoginEnabled, deviceFingerprint]);

  // Complete the login flow: save remembered account + redirect
  const completeLoginFlow = useCallback(async () => {
    console.log('[Login] Completing login flow...');
    console.log('[Login] Save state:', { isSaveLoginEnabled, deviceFingerprint: deviceFingerprint ? deviceFingerprint.substring(0, 20) + '...' : 'EMPTY' });
    if (isSaveLoginEnabled && deviceFingerprint) {
      localStorage.setItem('vastipost:remembered_email', email);
      localStorage.setItem('vastipost:save_login', 'true');
      try {
        console.log('[Login] Saving account to remembered accounts...', { deviceFingerprint: deviceFingerprint.substring(0, 20) + '...' });
        const saveResponse = await fetch('/api/auth/remembered-accounts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            deviceFingerprint,
            deviceName: navigator.userAgent.substring(0, 100),
          }),
        });
        if (saveResponse.ok) {
          console.log('[Login] Account saved to remembered accounts successfully');
        } else {
          const errorData = await saveResponse.json().catch(() => ({}));
          console.error('[Login] Failed to save remembered account:', saveResponse.status, errorData);
        }
      } catch (saveErr) {
        console.error('[Login] Failed to save remembered account (network error):', saveErr);
      }
    }
    // Full page navigation so layout.tsx re-runs buildAuthState() with the fresh session
    window.location.href = '/profilesettings';
  }, [email, isSaveLoginEnabled, deviceFingerprint]);

  // Handle 2FA verification success
  const handleTwoFactorVerified = useCallback(async () => {
    console.log('[Login] 2FA verified, updating session...');
    try {
      await updateSession({ twoFactorVerified: true });
      console.log('[Login] Session updated with twoFactorVerified=true');
    } catch (err) {
      console.error('[Login] Failed to update session:', err);
    }
    setShowTwoFactor(false);
    await completeLoginFlow();
  }, [updateSession, completeLoginFlow]);

  // Handle 2FA cancellation — sign out and reset
  const handleTwoFactorCancel = useCallback(async () => {
    console.log('[Login] 2FA cancelled, signing out...');
    setShowTwoFactor(false);
    await signOut({ redirect: false });
    setPassword('');
    setError('');
    restoreTwoFactorFocus();
  }, [restoreTwoFactorFocus]);

  // Handle Sign Up submission — validates inputs then shows onboarding modal
  // Account is NOT created until the user completes the onboarding flow
  const handleSignUp = useCallback(async () => {
    console.log('[SignUp] handleSignUp called');
    
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Validate email format
    if (!EMAIL_REGEX.test(email)) {
      setError('Invalid Email address');
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // All client-side validation passed — show onboarding modal
    setError('');
    setShowSignupOnboarding(true);
  }, [email, password, confirmPassword]);

  // Called when user completes BOTH steps of the onboarding modal during signup.
  // Creates the account + org + brand in a single API call, then auto-logs in.
  const handleSignupOnboardingComplete = useCallback(async (data?: OnboardingData) => {
    if (!data) return;

    console.log('[SignUp] Onboarding complete, creating account...');

    // Call comprehensive signup API that creates user + org + brand + settings
    const signupResponse = await fetch('/api/auth/signup-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        confirmPassword,
        displayName: data.displayName,
        timezone: data.timezone,
        brandName: data.brandName,
        industry: data.industry,
      }),
    });

    const signupData = await signupResponse.json();

    if (!signupResponse.ok) {
      // Throw so OnboardingModal shows the error in its banner
      throw new Error(signupData.error || 'Failed to create account');
    }

    console.log('[SignUp] Account created, auto-logging in...');

    // Auto-login after successful signup
    const result = await signIn('credentials', {
      email,
      password,
      redirect: false,
    });

    if (result?.error) {
      console.error('[SignUp] Auto-login failed:', result.error);
      setShowSignupOnboarding(false);
      setVariant('login');
      setError('Account created! Please log in.');
      return;
    }

    console.log('[SignUp] Auto-login successful, redirecting...');

    // Save to remembered accounts if enabled
    if (isSaveLoginEnabled && deviceFingerprint) {
      try {
        await fetch('/api/auth/remembered-accounts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            deviceFingerprint,
            deviceName: navigator.userAgent.substring(0, 100),
          }),
        });
      } catch (saveErr) {
        console.error('[SignUp] Failed to save remembered account:', saveErr);
      }
    }

    // Full page navigation so layout.tsx re-runs buildAuthState() with the fresh session
    window.location.href = '/profilesettings';
  }, [email, password, confirmPassword, isSaveLoginEnabled, deviceFingerprint]);

  // Called when user cancels the onboarding modal — go back to signup form
  const handleSignupOnboardingCancel = useCallback(() => {
    console.log('[SignUp] Onboarding cancelled');
    setShowSignupOnboarding(false);
  }, []);

  // Submit handler that routes to login or signup based on variant
  const handleSubmit = useCallback(async () => {
    if (variant === 'signup') {
      await handleSignUp();
    } else {
      await handleLogin();
    }
  }, [variant, handleLogin, handleSignUp]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[rgb(249,250,251)]">
      <div className="relative" style={{ width: 393, height: 851 }}>
        <HomeLoginInteractive
          email={email}
          password={password}
          confirmPassword={confirmPassword}
          variant={variant}
          onEmailChange={handleEmailChange}
          onPasswordChange={handlePasswordChange}
          onConfirmPasswordChange={handleConfirmPasswordChange}
          onVariantChange={handleVariantChange}
          onSubmit={handleSubmit}
          onSaveLoginChange={handleSaveLoginChange}
          initialSaveLogin={isSaveLoginEnabled}
          error={error}
          deviceFingerprint={deviceFingerprint}
          onAccountSelect={handleAccountSelect}
          style={{ width: 393, height: 851 }}
        />
        {isLoggingIn && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-[40px]">
            <div className="w-8 h-8 border-2 border-[#5ba9e1] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        <LoginTwoFactorModal
          isOpen={showTwoFactor}
          method={twoFactorMethod}
          userEmail={twoFactorEmail}
          userPhone={twoFactorPhone}
          onVerified={handleTwoFactorVerified}
          onCancel={handleTwoFactorCancel}
          returnFocusElement={twoFactorReturnFocusRef.current}
          getFallbackFocusElement={getTwoFactorFallbackFocusElement}
        />

        {/* Signup onboarding modal — shown after signup form validation, before account creation */}
        <OnboardingModal
          isOpen={showSignupOnboarding}
          mode="signup"
          initialName={email ? email.split('@')[0] : ''}
          initialEmail={email}
          initialTimezone={Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}
          onComplete={handleSignupOnboardingComplete}
          onCancel={handleSignupOnboardingCancel}
        />
      </div>
    </div>
  );
}
